// TypeScript port of Proximity.Models.Rules.BillingConfigResolver (C#).
//
// PARITY CONTRACT
// ===============
// This file and `server/Proximity.Model/Rules/BillingConfigResolver.cs` MUST implement the same
// resolution algorithm. Both are consulted at runtime:
//   - The C# version runs at JO submission inside JoHeaderDTO.Convert (last-line-of-defense).
//   - This TypeScript version runs at Program activation and at SupplierAccount delivery-method
//     change, blocking saves on inconsistency. This is the primary guardrail.
//
// Any change to the algorithm must be made in both files together. Keep the precedence table,
// effective-email chain, and consistency rule in lockstep.

import { ProgramBillingConfig, SupplierAccount, DocSubmitTypeEnum } from './entities/entity-model';

export interface TupleResolution {
  userGroupId: string | null;
  userGroupName: string | null;
  supplierId: string;
  supplierName: string;
  pbc: ProgramBillingConfig | null;
  effectiveEmail: string | null;
  /** Null if consistent; otherwise a human-readable reason. */
  inconsistencyReason: string | null;
}

export class BillingConfigResolver {

  /**
   * Resolve the PBC that would be selected for a given (programUserGroupId, supplierId) pair.
   * Precedence (most-specific first):
   *   1. (UserGroup match, SupplierId match)
   *   2. (UserGroup match, SupplierId null)
   *   3. (UserGroup null,  SupplierId match)
   *   4. (UserGroup null,  SupplierId null)
   * Returns null if no row matches.
   */
  static resolve(
    pbcs: readonly ProgramBillingConfig[],
    programUserGroupId: string | null,
    supplierId: string,
  ): ProgramBillingConfig | null {
    return (
      pbcs.find((p) => p.programUserGroupId === programUserGroupId && p.supplierId === supplierId) ??
      pbcs.find((p) => p.programUserGroupId === programUserGroupId && p.supplierId == null) ??
      pbcs.find((p) => p.programUserGroupId == null && p.supplierId === supplierId) ??
      pbcs.find((p) => p.programUserGroupId == null && p.supplierId == null) ??
      null
    );
  }

  /**
   * Walk the email-resolution chain: chosen PBC's InvoiceEmail → chosen PBC's Manifest.InvoiceEmail →
   * SupplierAccount.InvoiceEmail. Returns the trimmed value or null if every source is empty.
   */
  static resolveEffectiveEmail(
    pbc: ProgramBillingConfig | null,
    supplierAccount: SupplierAccount | null,
  ): string | null {
    const fromPbc = pbc?.invoiceEmail?.trim();
    if (fromPbc) return fromPbc;

    const fromManifest = pbc?.manifest?.invoiceEmail?.trim();
    if (fromManifest) return fromManifest;

    const fromSa = supplierAccount?.invoiceEmail?.trim();
    return fromSa || null;
  }

  /**
   * Check the consistency invariant for one (program, userGroup, supplier) tuple. Returns null if
   * consistent; otherwise a human-readable message naming the specific inconsistency.
   *
   * Rules:
   *   - SA delivery = Email or PrintAndEmail → effective email (PBC → Manifest → SA) must be non-empty.
   *   - SA delivery = Print or None         → the chosen PBC's InvoiceEmail must be empty
   *                                            (a non-empty value would be sent to the supplier
   *                                            but silently dropped at delivery time downstream).
   */
  static checkConsistency(
    pbc: ProgramBillingConfig | null,
    supplierAccount: SupplierAccount,
    programName: string,
    userGroupName: string | null,
    supplierName: string,
  ): string | null {
    const groupLabel = userGroupName ?? '[Default]';
    const delivery = supplierAccount.invoiceDocSubmitTypeId;

    if (delivery === DocSubmitTypeEnum.Email || delivery === DocSubmitTypeEnum.PrintAndEmail) {
      const email = this.resolveEffectiveEmail(pbc, supplierAccount);
      if (!email) {
        return (
          `Program "${programName}", User Group "${groupLabel}", Supplier "${supplierName}": ` +
          `supplier invoice delivery is ${DocSubmitTypeEnum[delivery]} but no invoice email is configured ` +
          `(check the Program Billing Config, the Manifest, and the Supplier Account).`
        );
      }
    } else {
      // Print or None: a PBC-level InvoiceEmail will be sent over but silently dropped at delivery.
      const pbcEmail = pbc?.invoiceEmail?.trim();
      if (pbcEmail) {
        return (
          `Program "${programName}", User Group "${groupLabel}", Supplier "${supplierName}": ` +
          `Program Billing Config has an Invoice E-Mail, but the Supplier Account's invoice delivery is ${DocSubmitTypeEnum[delivery]}. ` +
          `Either clear the email or set the Supplier Account's invoice delivery to Email or PrintAndEmail.`
        );
      }
    }

    return null;
  }

  /**
   * Enumerate every (UserGroup × Supplier) tuple for a program and resolve each. Yields one result
   * per tuple, including consistent ones. Callers filter by `inconsistencyReason`.
   *
   * The "no user group" tuple (userGroupId = null) is always included to cover JOs submitted without
   * a user-group context.
   *
   * @param pbcs The program's ProgramBillingConfigs.
   * @param programName Used only for human-readable error messages.
   * @param programSuppliers Suppliers served by this program. Each entry must have a `supplier`
   *   navigation (`{ id, name }`).
   * @param userGroups User groups for this program (via approvalTree.approvalTreeUserGroups).
   *   The null user-group case is included automatically.
   * @param supplierAccountsBySupplierId Account's SupplierAccounts keyed by supplierId. Suppliers
   *   without an entry are skipped (a different validation problem — every program supplier should
   *   have a SupplierAccount on the account).
   */
  static resolveAll(
    pbcs: readonly ProgramBillingConfig[],
    programName: string,
    programSuppliers: readonly { supplier: { id: string; name: string } }[],
    userGroups: readonly { id: string; name: string }[],
    supplierAccountsBySupplierId: Map<string, SupplierAccount>,
  ): TupleResolution[] {
    const groups: ({ id: string; name: string } | null)[] = [null, ...userGroups];
    const results: TupleResolution[] = [];

    for (const ps of programSuppliers) {
      const sa = supplierAccountsBySupplierId.get(ps.supplier.id);
      if (!sa) continue;
      for (const ug of groups) {
        const pbc = this.resolve(pbcs, ug?.id ?? null, ps.supplier.id);
        const effectiveEmail = this.resolveEffectiveEmail(pbc, sa);
        const inconsistencyReason = this.checkConsistency(pbc, sa, programName, ug?.name ?? null, ps.supplier.name);
        results.push({
          userGroupId: ug?.id ?? null,
          userGroupName: ug?.name ?? null,
          supplierId: ps.supplier.id,
          supplierName: ps.supplier.name,
          pbc,
          effectiveEmail,
          inconsistencyReason,
        });
      }
    }

    return results;
  }
}
